import { Component, OnInit, Input, AfterContentInit, EventEmitter, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CountryISO, PhoneNumberFormat, SearchCountryField } from 'ngx-intl-tel-input';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { EmailValidator } from 'src/app/shared/validators/email.validators';
import { IDFormatValidator } from 'src/app/shared/validators/id-format.validator';
import { IDLengthValidator } from 'src/app/shared/validators/id-length.validator';
import { LettersOnlyValidator } from 'src/app/shared/validators/letters-only.validators';
import { RequiredValidator } from 'src/app/shared/validators/required.validator';
import { UniqueDocumentLaftValidator } from 'src/app/shared/validators/unique-document-laft.validator';
import { AddressValidator } from 'src/app/shared/validators/address.validator';
import { LocationService } from './location.service';
import { IDFormatPartnerValidator } from 'src/app/shared/validators/id-format-partner.validator';
import { IDLengthPartnerValidator } from 'src/app/shared/validators/id-length-partner.validator';
import { PartnerIdDifferentValidator } from 'src/app/shared/validators/partner-id-different.validator';
import { FormService } from 'src/app/shared/services/form.service';

@Component({
  selector: 'app-laft-personal-data-form',
  templateUrl: './laft-personal-data-form.component.html',
  styleUrls: ['./laft-personal-data-form.component.scss'],
})
export class LaftPersonalDataFormComponent implements OnInit, AfterContentInit {
  @Input() userData: any;
  @Input() initData: any;
  @Output() nextStep = new EventEmitter();
  @Output() formChangesEvent = new EventEmitter();

  formSubmitSubject$ = new Subject();
  ngUnsubscribe: Subject<void> = new Subject<void>();

  public preferredCountries: CountryISO[] = [CountryISO.Peru, CountryISO.Spain];
  public searchCountryField = SearchCountryField;
  public countryISO = CountryISO;
  public phoneNumberFormat = PhoneNumberFormat;
  public phoneNumberMaxDigit = 9;
  public phoneError = '';
  public idTypeOptions = [
    { value: 'national id', text: 'DNI' },
    { value: 'foreigner card', text: 'Carnet de Extranjería' },
    { value: 'passport', text: 'Pasaporte' },
  ];

  public idPartnerTypeOptions = [
    { value: 'national id', text: 'DNI' },
    { value: 'foreigner card', text: 'Carnet de Extranjería' },
    { value: 'passport', text: 'Pasaporte' },
  ];

  public educationLevelOptions = [
    { value: 'high_school', text: 'Secundaria completa' },
    { value: 'technical', text: 'Técnica completo' },
    { value: 'university', text: 'Universitario completo' },
    { value: 'master', text: 'Máster' },
    { value: 'doctorate', text: 'Doctorado' },
  ];

  public economicActivityTypeOptions = [
    { value: 'employed', text: 'Trabajador dependiente' },
    { value: 'self_employed', text: 'Trabajador independiente' },
  ];

  public annualIncomeOptions = [
    { value: 'more_than_500k', text: 'Igual o superior a 500,000 soles' },
    { value: '250k_to_500k', text: 'Entre 250,000 y 500,000 soles' },
    { value: '125k_to_250k', text: 'Entre 125,000 y 250,000 soles' },
    { value: 'less_than_125k', text: 'Menor a 125,000 soles' },
  ];

  public maritalStatusOptions = [
    { value: 'single', text: 'Soltero' },
    { value: 'married', text: 'Casado' },
    { value: 'divorced', text: 'Divorciado' },
    { value: 'widowed', text: 'Viudo' },
    { value: 'cohabiting', text: 'Conviviente' },
  ];

  public mainEconomicActivityOptions = [
    { value: 'servicios', text: 'Servicios' },
    { value: 'minería', text: 'Minería' },
    { value: 'legal', text: 'Legal' },
    { value: 'financiero', text: 'Financiero' },
    { value: 'inmobiliario', text: 'Inmobiliario' },
    { value: 'construcción', text: 'Construcción' },
    { value: 'otros', text: 'Otros' },
  ];

  public estimatedPatrimonyOptions= [
    { value: 'Menor a 250,000 soles', text: 'Menor a 250,000 soles' },
    { value: 'Entre 250,000 y 500,000 soles', text: 'Entre 250,000 y 500,000 soles' },
    { value: 'Igual o superior a 500,000 soles', text: 'Igual o superior a 500,000 soles' },
  ];

  MARRIED_STATUS = 'married';
  COHABITING_STATUS = 'cohabiting';

  private isPartnerStatus(status: string): boolean {
    return status === this.MARRIED_STATUS || status === this.COHABITING_STATUS;
  }
  public personalDataForm: FormGroup;
  departmentOptions;
  countryOptions;
  provinceOptions;
  districtOptions;
  isDepartmentDisabled = true;
  isProvinceDisabled = true;
  isDistrictDisabled = true;
  selectedIdType;
  separateProperty = false;
  PERU = 'Perú';

  constructor(
    private formBuilder: FormBuilder,
    private uniqueDocumentValidator: UniqueDocumentLaftValidator,
    private locationService: LocationService,
    private service: FormService
  ) {}

  ngOnInit(): void {
    this.personalDataForm = this.formBuilder.group({
      names: ['', [RequiredValidator, LettersOnlyValidator, Validators.minLength(2), Validators.maxLength(50)]],
      firstLastName: ['', [RequiredValidator, LettersOnlyValidator, Validators.minLength(2), Validators.maxLength(50)]],
      secondLastName: ['', [LettersOnlyValidator, Validators.maxLength(50)]],
      nationality: ['', [Validators.required]],
      idType: ['', [Validators.required]],
      idNumber: [
        '',
        [RequiredValidator, IDFormatValidator, IDLengthValidator],
        [this.uniqueDocumentValidator.isUniqueDocument(this.userData?.idNumber)],
      ],
      cellphone: ['', [Validators.required]],
      email: ['', [Validators.required, EmailValidator]],
      educationLevel: ['', [Validators.required]],
      occupation: ['', [Validators.required]],
      economicActivityType: ['', [Validators.required]],
      mainEconomicActivity: ['', [Validators.required]],
      mainEconomicActivityOther: ['', []],
      annualIncome: ['', [Validators.required]],
      birthDate: ['', [Validators.required]],
      maritalStatus: ['', [Validators.required]],
      department: ['', []],
      province: ['', []],
      district: ['', []],
      address: ['', [Validators.required, AddressValidator]],
      partnerIdType: ['', []],
      partnerNames: ['', []],
      partnerLastNames: ['', []],
      idCertificatePicture: ['', []],
      partnerIdNumber: ['', []],
      separateProperty: [false, []],
      country: ['', [Validators.required]],
    });

    if(this.userData.segment && this.userData.segment !== 'menos de s/ 5 000'){
      this.personalDataForm.addControl('estimatedPatrimony', new FormControl('', Validators.required));
    }

    this.personalDataForm.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
      const cleanedData = this.removeEmptyFields(this.personalDataForm.value);
      this.formChangesEvent.emit(cleanedData);
    });

    this.formSubmitSubject$
      .pipe(
        tap(() => {
          this.personalDataForm.markAsDirty();
          this.personalDataForm.markAllAsTouched();
          Object.keys(this.personalDataForm.controls).forEach(key => {
            const control = this.personalDataForm.get(key);
            control?.updateValueAndValidity({ emitEvent: true });
          });
        }),
        switchMap(() =>
          this.personalDataForm.statusChanges.pipe(
            startWith(this.personalDataForm.status),
            filter(status => status !== 'PENDING'),
            take(1)
          )
        ),
        filter(status => status === 'VALID')
      )
      .subscribe(() => {
        const cleanedData = this.removeEmptyFields(this.personalDataForm.value);
        this.nextStep.next(cleanedData);
      });

    this.getAllCountries();
    this.loadDepartments();
    this.setupLocationCascade();
    this.maritalStatusActions();
    this.handleMainEconomicActivityValidation();

    this.personalDataForm
      .get('country')
      ?.valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(country => {
        this.handleCountryLocationValidation(country);
      });

    this.personalDataForm
      .get('nationality')
      ?.valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(nationality => {
        this.handleSecondLastNameValidation(nationality);
      });

    this.personalDataForm
      .get('partnerIdType')
      ?.valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(partnerIdType => {
        if (partnerIdType) {
          this.selectedIdType = partnerIdType;
          const maritalStatus = this.personalDataForm.get('maritalStatus')?.value;
          if (this.isPartnerStatus(maritalStatus)) {
            this.updateDocumentValidations();
          }
        }
      });

    this.personalDataForm
      .get('idNumber')
      ?.valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        const maritalStatus = this.personalDataForm.get('maritalStatus')?.value;
        if (this.isPartnerStatus(maritalStatus)) {
          const partnerIdNumberControl = this.personalDataForm.get('partnerIdNumber');
          if (partnerIdNumberControl) {
            partnerIdNumberControl.updateValueAndValidity({ emitEvent: false });
          }
        }
      });

    this.personalDataForm
      .get('separateProperty')
      ?.valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(separatePropertyValue => {
        this.separateProperty = separatePropertyValue;
        const maritalStatus = this.personalDataForm.get('maritalStatus')?.value;
        if (this.isPartnerStatus(maritalStatus)) {
          this.updateDocumentValidations();
        }
      });

    if (this.userData) {
      this.populateFormWithUserData();
    }
  }

  handleMainEconomicActivityValidation() {
    this.personalDataForm
      .get('mainEconomicActivity')
      ?.valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        const otherControl = this.personalDataForm.get('mainEconomicActivityOther');
        if (value === 'otros') {
          otherControl.setValidators([Validators.required]);
        } else {
          otherControl.clearValidators();
          otherControl.setValue('');
        }
        otherControl.updateValueAndValidity();
      });
  }

  maritalStatusActions() {
    this.personalDataForm
      .get('maritalStatus')
      ?.valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(maritalStatus => {
        if (this.isPartnerStatus(maritalStatus)) {
          this.personalDataForm.get('partnerNames')?.setValidators([Validators.required]);
          this.personalDataForm.get('partnerLastNames')?.setValidators([Validators.required]);
          this.personalDataForm.get('partnerIdType')?.setValidators([Validators.required]);
          this.personalDataForm
            .get('partnerIdNumber')
            ?.setValidators([Validators.required, IDFormatPartnerValidator, IDLengthPartnerValidator, PartnerIdDifferentValidator]);

          const partnerIdType = this.personalDataForm.get('partnerIdType')?.value;
          if (partnerIdType && !this.selectedIdType) {
            this.selectedIdType = partnerIdType;
          }

          const separatePropertyValue = this.personalDataForm.get('separateProperty')?.value;
          if (separatePropertyValue !== null && separatePropertyValue !== undefined) {
            this.separateProperty = separatePropertyValue;
          }

          this.updateDocumentValidations();
        } else {
          this.personalDataForm.get('partnerNames')?.clearValidators();
          this.personalDataForm.get('partnerLastNames')?.clearValidators();
          this.personalDataForm.get('partnerIdType')?.clearValidators();
          this.personalDataForm.get('partnerIdNumber')?.clearValidators();
          this.personalDataForm.get('idCertificatePicture')?.clearValidators();

          this.personalDataForm.patchValue({
            partnerNames: '',
            partnerLastNames: '',
            partnerIdType: '',
            partnerIdNumber: '',
            idCertificatePicture: '',
          });

          this.separateProperty = false;
          this.selectedIdType = null;
        }

        this.personalDataForm.get('partnerNames')?.updateValueAndValidity();
        this.personalDataForm.get('partnerLastNames')?.updateValueAndValidity();
        this.personalDataForm.get('partnerIdType')?.updateValueAndValidity();
        this.personalDataForm.get('partnerIdNumber')?.updateValueAndValidity();
        this.personalDataForm.get('idCertificatePicture')?.updateValueAndValidity();
      });
  }

  private loadDepartments(): void {
    this.locationService
      .getDepartments()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(departments => {
        this.departmentOptions = departments.map(dept => ({
          value: dept,
          text: dept,
        }));
      });
  }

  ngAfterContentInit(): void {
    this.personalDataForm
      .get('cellphone')
      ?.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.handlePhoneValidation();
      });
  }

  private populateFormWithUserData(): void {
    let firstLastName = '';
    let secondLastName = '';

    if (this.userData.familyNames) {
      const words = this.userData.familyNames.trim().split(/\s+/);
      if (words.length > 0) {
        firstLastName = words[0];
        secondLastName = words.length > 1 ? words.slice(1).join(' ') : '';
      }
    }

    this.personalDataForm.patchValue({
      names: this.userData.names || '',
      firstLastName: firstLastName,
      secondLastName: secondLastName,
      idType: this.userData.idType || '',
      idNumber: this.userData.idNumber || '',
      cellphone: this.userData.phone || '',
      nationality: this.userData.country || '',
      email: this.userData.email || '',
      educationLevel: this.initData?.educationLevel || '',
      occupation: this.initData?.occupation || '',
      economicActivityType: this.initData?.economicActivityType || '',
      mainEconomicActivity: this.initData?.mainEconomicActivity || '',
      annualIncome: this.initData?.annualIncome || '',
      birthDate: this.initData?.birthDate || '',
      country: this.initData?.country || '',
      maritalStatus: this.initData?.maritalStatus || '',
      department: this.initData?.department || '',
      province: this.initData?.province || '',
      district: this.initData?.district || '',
      address: this.initData?.address || '',
      partnerIdType: this.initData?.partnerIdType || '',
      partnerNames: this.initData?.partnerNames || '',
      partnerLastNames: this.initData?.partnerLastNames || '',
      idCertificatePicture: this.initData?.idCertificatePicture || '',
      partnerIdNumber: this.initData?.partnerIdNumber || '',
      mainEconomicActivityOther: this.initData?.mainEconomicActivityOther || '',
      separateProperty: this.initData?.separateProperty || false,
    });

    if(this.personalDataForm.get('estimatedPatrimony') && this.initData?.estimatedPatrimony){
       this.personalDataForm.patchValue({
        estimatedPatrimony: this.initData?.estimatedPatrimony
       });
    }

    if (this.initData?.partnerIdType) {
      this.selectedIdType = this.initData.partnerIdType;
    }
    if (this.initData?.separateProperty !== null && this.initData?.separateProperty !== undefined) {
      this.separateProperty = this.initData.separateProperty;
    }

    const maritalStatus = this.personalDataForm.get('maritalStatus')?.value;
    if (this.isPartnerStatus(maritalStatus)) {
      this.updateDocumentValidations();
    }

    const nationality = this.personalDataForm.get('nationality')?.value;
    if (nationality) {
      this.handleSecondLastNameValidation(nationality);
    }
  }

  onContinue(): void {
    this.formSubmitSubject$.next();
  }

  countryChange(event: any): void {
    if (event?.dialCode == '51') {
      this.phoneNumberMaxDigit = 9;
    } else {
      this.phoneNumberMaxDigit = 15;
    }
  }

  focusOutPhone(): void {
    this.handlePhoneValidation();
  }

  private handlePhoneValidation(): void {
    const cellphoneControl = this.personalDataForm.controls?.cellphone;
    if (cellphoneControl?.touched) {
      if (cellphoneControl?.errors?.required) {
        this.phoneError = 'obligatorio';
      } else if (
        cellphoneControl?.errors?.hasOwnProperty('validatePhoneNumber') &&
        !cellphoneControl?.errors?.validatePhoneNumber?.valid
      ) {
        this.phoneError = 'formato incorrecto';
      } else {
        this.phoneError = '';
      }
    }
  }

  private setupLocationCascade(): void {
    this.personalDataForm
      .get('department')
      ?.valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(department => {
        this.provinceOptions = [];
        this.districtOptions = [];
        this.isProvinceDisabled = true;
        this.isDistrictDisabled = true;

        if (department) {
          const departmentText = this.departmentOptions.find(d => d.value === department)?.text;
          if (departmentText) {
            this.locationService
              .getProvincesByDepartment(departmentText)
              .pipe(takeUntil(this.ngUnsubscribe))
              .subscribe(provinces => {
                this.provinceOptions = provinces.map(prov => ({
                  value: prov.toLowerCase().replace(/\s+/g, '_'),
                  text: prov,
                }));
                this.isProvinceDisabled = false;
              });
          }
        }
      });

    this.personalDataForm
      .get('province')
      ?.valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(province => {
        this.districtOptions = [];

        if (province) {
          const department = this.personalDataForm.get('department')?.value;
          const departmentText = this.departmentOptions.find(d => d.value === department)?.text;
          const provinceText = this.provinceOptions.find(p => p.value === province)?.text;

          if (departmentText && provinceText) {
            this.locationService
              .getDistrictsByProvince(departmentText, provinceText)
              .pipe(takeUntil(this.ngUnsubscribe))
              .subscribe(districts => {
                this.districtOptions = districts.map(dist => ({
                  value: dist.toLowerCase().replace(/\s+/g, '_'),
                  text: dist,
                }));
                this.isDistrictDisabled = false;
              });
          }
        }
      });

    if (this.personalDataForm.get('country').value == this.PERU) {
      this.isProvinceDisabled = true;
      this.isDistrictDisabled = true;
    }
  }

  idTypeChanged(newType) {
    this.selectedIdType = newType;
    const maritalStatus = this.personalDataForm.get('maritalStatus')?.value;
    if (this.isPartnerStatus(maritalStatus)) {
      this.updateDocumentValidations();
    }
  }

  setOption(separatePropertyValue) {
    this.separateProperty = separatePropertyValue;
    this.updateDocumentValidations();
  }

  private updateDocumentValidations() {
    const certificateControl = this.personalDataForm.get('idCertificatePicture');
    const maritalStatus = this.personalDataForm.get('maritalStatus')?.value;

    certificateControl?.clearValidators();

    if (this.isPartnerStatus(maritalStatus)) {
      if (this.separateProperty) {
        certificateControl?.setValidators([Validators.required]);
      } else {
        certificateControl?.clearValidators();
        certificateControl?.setValue('');
      }
    }

    certificateControl?.updateValueAndValidity();
  }

  getAllCountries() {
    this.service.getAllCountries().subscribe((data: any) => {
      this.countryOptions = data.map(dept => ({
        value: dept.nameSpanish,
        text: dept.nameSpanish,
      }));
    });
  }

  handleCountryLocationValidation(country: String) {
    const departmentControl = this.personalDataForm.get('department');
    const provinceControl = this.personalDataForm.get('province');
    const districtControl = this.personalDataForm.get('district');

    if (country === this.PERU) {
      this.isDepartmentDisabled = false;
      departmentControl?.setValidators([Validators.required]);
      provinceControl?.setValidators([Validators.required]);
      districtControl?.setValidators([Validators.required]);
    } else {
      departmentControl?.clearValidators();
      provinceControl?.clearValidators();
      districtControl?.clearValidators();

      departmentControl?.setValue('');
      provinceControl?.setValue('');
      districtControl?.setValue('');

      departmentControl?.markAsUntouched();
      provinceControl?.markAsUntouched();
      districtControl?.markAsUntouched();

      departmentControl?.markAsPristine();
      provinceControl?.markAsPristine();
      districtControl?.markAsPristine();

      this.isDepartmentDisabled = true;
      this.isProvinceDisabled = true;
      this.isDistrictDisabled = true;
    }

    departmentControl?.updateValueAndValidity({ emitEvent: false });
    provinceControl?.updateValueAndValidity({ emitEvent: false });
    districtControl?.updateValueAndValidity({ emitEvent: false });
  }

  handleSecondLastNameValidation(nationality: String) {
    const secondLastNameControl = this.personalDataForm.get('secondLastName');

    if (nationality === this.PERU) {
      secondLastNameControl?.setValidators([RequiredValidator, LettersOnlyValidator, Validators.maxLength(50)]);
    } else {
      secondLastNameControl?.clearValidators();
      secondLastNameControl?.setValidators([LettersOnlyValidator, Validators.maxLength(50)]);
    }

    secondLastNameControl?.updateValueAndValidity({ emitEvent: false });
  }

  removeEmptyFields(data: any): any {
    const cleaned = { ...data };

    if (cleaned.cellphone) {
      const phone = cleaned.cellphone;
      cleaned.phone = phone?.number?.replace(/ /g, '');
      cleaned.phoneCode = phone?.dialCode;
      cleaned.phoneCountry = phone?.countryCode;
      delete cleaned.cellphone;
    }

    if (cleaned.firstLastName) {
      const firstLastName = cleaned.firstLastName || '';
      const secondLastName = cleaned.secondLastName || '';
      cleaned.familyNames = [firstLastName, secondLastName].filter(name => name.trim()).join(' ').trim();
    }

    if (cleaned.separateProperty) {
      if (!cleaned.idCertificatePicture || cleaned.idCertificatePicture.trim() === '') {
        cleaned.idCertificatePicture = '';
      }
    } else {
      cleaned.idCertificatePicture = '';
    }

    if (!cleaned.partnerIdType || cleaned.partnerIdType.trim() === '') {
      cleaned.partnerIdType = '';
    }

    if (!cleaned.partnerIdNumber || cleaned.partnerIdNumber.trim() === '') {
      cleaned.partnerIdNumber = '';
    }

    if (!cleaned.partnerLastNames || cleaned.partnerLastNames.trim() === '') {
      cleaned.partnerLastNames = '';
    }

    if (!cleaned.partnerNames || cleaned.partnerNames.trim() === '') {
      cleaned.partnerNames = '';
    }

    if (cleaned.mainEconomicActivity !== 'otros') {
      cleaned.mainEconomicActivityOther = '';
    }

    if (cleaned.country !== this.PERU) {
      cleaned.department = '';
      cleaned.province = '';
      cleaned.district = '';
    }

    return cleaned;
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
